<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'

export interface RainAlertEntry {
  id: string
  ruleId: string
  ruleName: string
  firedAt: string
  eventSnapshot: Record<string, unknown>
  channelResults: { channel: string; success: boolean; error?: string }[]
}

const props = withDefaults(
  defineProps<{
    entries: RainAlertEntry[]
    isDarkMode: boolean
    selectedId?: string | null
    /** Latest entry id to play rain-in animation */
    animateDropId?: string | null
    /** One column per rule in this order, including empty buckets */
    allRules?: { id: string; name: string }[]
    /** compact = narrow sidebar; full = main Buckets tab */
    layout?: 'compact' | 'full'
    /** Header label */
    title?: string
    /** Distance from viewport top where the drop starts falling (px) */
    rainFromTopPx?: number
  }>(),
  {
    layout: 'compact',
    title: 'Buckets',
    rainFromTopPx: 72,
  },
)

const emit = defineEmits<{ (e: 'select', entry: RainAlertEntry): void }>()

const MAX_DROPS_PER_BUCKET = 120

const buckets = computed(() => {
  const map = new Map<string, { ruleName: string; items: RainAlertEntry[] }>()
  for (const e of props.entries) {
    if (!map.has(e.ruleId)) map.set(e.ruleId, { ruleName: e.ruleName, items: [] })
    map.get(e.ruleId)!.items.push(e)
  }

  const ruleOrder: { id: string; name: string }[] =
    props.allRules?.length
      ? props.allRules.map((r) => ({ id: r.id, name: r.name }))
      : [...map.keys()].map((id) => ({ id, name: map.get(id)!.ruleName }))

  return ruleOrder.map(({ id: ruleId, name: ruleNameFallback }) => {
    const b = map.get(ruleId)
    const ruleName = b?.ruleName ?? ruleNameFallback
    const sorted = b
      ? [...b.items].sort(
          (a, b) => new Date(b.firedAt).getTime() - new Date(a.firedAt).getTime(),
        )
      : []
    const trimmed =
      sorted.length > MAX_DROPS_PER_BUCKET
        ? sorted.slice(0, MAX_DROPS_PER_BUCKET)
        : sorted
    return { ruleId, ruleName, items: trimmed }
  })
})

const isFull = computed(() => props.layout === 'full')

function colorForRule(ruleId: string): string {
  let h = 2166136261
  for (let i = 0; i < ruleId.length; i++) {
    h ^= ruleId.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  const hue = (h >>> 0) % 360
  const l = props.isDarkMode ? 52 : 42
  return `hsl(${hue} 72% ${l}%)`
}

/** In-flight clone (viewport coords); real drop hidden until landing */
const flying = ref<{
  id: string
  ruleId: string
  left: number
  width: number
  height: number
  topStart: number
  topEnd: number
} | null>(null)

let flightAnim: Animation | null = null

function cancelFlight() {
  if (flightAnim) {
    try {
      flightAnim.cancel()
    } catch {}
    flightAnim = null
  }
  flying.value = null
}

watch(
  () => props.animateDropId,
  async (id) => {
    if (!id) {
      cancelFlight()
      return
    }
    cancelFlight()
    await nextTick()

    const target = document.querySelector(`[data-rain-drop-id="${id}"]`) as HTMLElement | null
    if (!target) return
    const entry = props.entries.find((e) => e.id === id)
    if (!entry) return
    const rect = target.getBoundingClientRect()
    if (rect.width < 1 || rect.height < 1) return

    // Fall from top-of-page band when there is room; always start above the landing slot
    const topStart =
      rect.top > props.rainFromTopPx + 20
        ? props.rainFromTopPx
        : Math.max(8, rect.top - 28)

    flying.value = {
      id,
      ruleId: entry.ruleId,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      topStart,
      topEnd: rect.top,
    }

    await nextTick()
    const flyEl = document.querySelector('[data-rain-flying-drop]') as HTMLElement | null
    if (!flyEl) {
      flying.value = null
      return
    }

    flyEl.style.top = `${topStart}px`

    const anim = flyEl.animate(
      [
        { top: `${topStart}px`, opacity: 0.82, transform: 'scaleX(1.08)' },
        { top: `${rect.top}px`, opacity: 1, transform: 'scaleX(1)' },
      ],
      {
        duration: 640,
        easing: 'cubic-bezier(0.22, 0.92, 0.28, 1)',
        fill: 'forwards',
      },
    )

    flightAnim = anim
    anim.onfinish = () => {
      flightAnim = null
      flying.value = null
    }
  },
)

onUnmounted(() => cancelFlight())
</script>

<template>
  <div class="flex flex-col h-full min-h-0 min-w-0">
    <div
      class="flex-shrink-0 px-2 py-1.5 border-b text-[9px] font-semibold uppercase tracking-wide leading-tight"
      :class="isDarkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-500'"
      title="Each column is one alert rule. The stack sits at the bottom of the bucket; new drops land on top of the pile (newest highest, oldest lowest)."
    >
      {{ title }}
    </div>
    <div
      class="flex-1 min-h-0 flex flex-row items-stretch gap-1 px-1 py-2 overflow-x-auto overflow-y-auto rain-scroll"
      :class="isFull ? 'gap-1.5 px-2' : ''"
    >
      <div
        v-for="bucket in buckets"
        :key="bucket.ruleId"
        class="flex flex-col flex-shrink-0 h-full min-w-0 border rounded-md overflow-hidden"
        :class="[
          isFull ? 'flex-1 min-w-[36px] max-w-[120px]' : 'w-[18px]',
          isDarkMode ? 'border-gray-700/80 bg-gray-900/40' : 'border-gray-200 bg-white/50',
        ]"
        :title="bucket.ruleName"
      >
        <div
          class="flex-shrink-0 px-0.5 pt-1 pb-0.5 text-center leading-tight border-b"
          :class="[
            isFull ? 'text-[10px] px-1 min-h-[2.25rem]' : 'text-[8px]',
            isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600',
          ]"
        >
          <span
            class="line-clamp-2 break-words w-full font-medium"
            :title="bucket.ruleName"
          >{{ isFull ? bucket.ruleName : bucket.ruleName.charAt(0).toUpperCase() }}</span>
        </div>
        <div
          class="flex-1 min-h-0 flex flex-col justify-end gap-0.5 overflow-y-auto overflow-x-hidden px-1 py-1 rain-col-scroll"
          :class="isFull ? 'gap-1' : ''"
        >
          <button
            v-for="entry in bucket.items"
            :key="entry.id"
            type="button"
            :data-rain-drop-id="entry.id"
            class="rain-drop w-full rounded-[2px] flex-shrink-0 border transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/80"
            :class="[
              selectedId === entry.id
                ? 'ring-2 ring-orange-400 z-10'
                : isDarkMode
                  ? 'border-white/10 hover:border-white/25'
                  : 'border-black/10 hover:border-black/20',
              flying?.id === entry.id ? 'rain-drop--hidden' : '',
            ]"
            :style="{
              height: isFull ? '8px' : '6px',
              backgroundColor: colorForRule(bucket.ruleId),
            }"
            :title="`${bucket.ruleName}\n${new Date(entry.firedAt).toLocaleString()}`"
            @click="emit('select', entry)"
          />
          <div
            v-if="bucket.items.length === 0"
            class="flex-1 min-h-[24px] flex items-center justify-center text-[9px] opacity-50 select-none"
            :class="isDarkMode ? 'text-gray-600' : 'text-gray-400'"
          >
            —
          </div>
        </div>
      </div>
    </div>

    <!-- Falls from top of viewport; not clipped by bucket overflow -->
    <Teleport to="body">
      <div
        v-if="flying"
        data-rain-flying-drop
        class="rain-fly fixed z-[9999] rounded-[2px] border pointer-events-none shadow-lg"
        :class="isDarkMode ? 'border-white/20' : 'border-black/15'"
        :style="{
          left: `${flying.left}px`,
          width: `${flying.width}px`,
          height: `${flying.height}px`,
          top: `${flying.topStart}px`,
          backgroundColor: colorForRule(flying.ruleId),
        }"
      />
    </Teleport>
  </div>
</template>

<style scoped>
.rain-scroll {
  scrollbar-width: thin;
}
.rain-col-scroll {
  scrollbar-width: thin;
}

.rain-drop--hidden {
  opacity: 0;
  visibility: hidden;
}

/* Teleport content is not scoped to child — use :deep or global for flying */
</style>

<style>
/* Flying clone lives in body (Teleport); must be unscoped */
[data-rain-flying-drop] {
  box-sizing: border-box;
}
</style>
